import { type RefObject, useEffect, useState } from "react";
import type { ListImperativeAPI } from "react-window";

/**
 * Anything that can yield the scrolling element:
 *
 * - `HTMLElement` — a plain scroll container captured as state (`ref={setEl}`). **Preferred** for
 *   anything that isn't a react-window list.
 * - `ListImperativeAPI` — a react-window list, from its state-backed `useListCallbackRef`.
 * - `RefObject` — a classic React ref. Convenient, but see the caveat on the hook below.
 * - `null` — nothing on screen yet.
 */
export type ScrollbarWidthTarget = HTMLElement | ListImperativeAPI | RefObject<HTMLElement | null> | null;

const resolveScrollElement = (target: ScrollbarWidthTarget): HTMLElement | null => {
	if (!target) return null;
	// react-window exposes its outermost (scrolling) node behind an imperative API.
	if ("element" in target) return target.element;
	if ("current" in target) return target.current;
	return target;
};

/**
 * Measures the width consumed by a scroll container's vertical scrollbar, so a sibling header
 * rendered outside it can reserve the same gutter (`paddingRight`) and keep its columns aligned with
 * the scrolled content below. Returns 0 when there is nothing to measure, or on platforms whose
 * overlay scrollbars take no layout space.
 *
 * Give it the element (or react-window API) **as a value**, so that its arrival re-runs the
 * measurement on its own:
 *
 * ```ts
 * const [list, setList] = useListCallbackRef();        // react-window
 * const scrollbarWidth = useListScrollbarWidth(list);
 * <VirtualizedList listRef={setList} … />
 *
 * const [scroller, setScroller] = useState<HTMLElement | null>(null);   // any other container
 * const scrollbarWidth = useListScrollbarWidth(scroller);
 * <Box ref={setScroller} sx={{ overflowY: "auto" }}>…</Box>
 * ```
 *
 * A `RefObject` is also accepted, but carries a caveat: mutating a ref doesn't re-render, so if the
 * element isn't mounted yet when this effect first runs, nothing will schedule another attempt and
 * the width stays 0 for good. Only pass a ref when the element is guaranteed to exist on mount;
 * otherwise use one of the value forms above.
 *
 * Once attached, the `ResizeObserver` catches later scrollbar appearance/disappearance (the content
 * box shrinks when a scrollbar shows).
 */
export const useListScrollbarWidth = (target: ScrollbarWidthTarget): number => {
	const [scrollbarWidth, setScrollbarWidth] = useState(0);

	useEffect(() => {
		const el = resolveScrollElement(target);
		if (!el) {
			// Nothing on screen means no gutter to reserve.
			setScrollbarWidth(0);
			return;
		}
		const measure = () => setScrollbarWidth(el.offsetWidth - el.clientWidth);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, [target]);

	return scrollbarWidth;
};
